/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : QuestionNode.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Handle, Position } from "reactflow";
import { Box, Typography, TextField, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { AccountTreeOutlined } from "@mui/icons-material";
import { registerNode } from "../registry/NodeRegistry";
import NodeToolbar from "./NodeToolbar";
import { useState } from "react";
import { componentStyle } from "../../../config/appTheme";

function QuestionNode({ data, selected }) {
  const { nodeId, questionText, answers, updateNodeData } = data;
  const [hovered, setHovered] = useState(false);

  /* -----------------------------
     IMPORTANT:
     React Flow captures mouse events.
     These helpers unblock inputs.
  ----------------------------- */
  const stop = (e) => e.stopPropagation();

  /* -------------------------------
     UPDATE HELPERS
  ------------------------------- */
  const updateQuestion = (value) => {
    updateNodeData(nodeId, { questionText: value });
  };

  const updateAnswer = (answerId, value) => {
    updateNodeData(nodeId, {
      answers: answers.map((a) =>
        a.id === answerId ? { ...a, label: value } : a,
      ),
    });
  };

  const addAnswer = () => {
    updateNodeData(nodeId, {
      answers: [...answers, { id: crypto.randomUUID(), label: "New option" }],
    });
  };

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{ position: "relative" }}
    >
      <NodeToolbar
        visible={hovered || selected}
        onDelete={() => data.onDeleteNode(nodeId)}
      />
      <Box
        className="pm-node-wrapper"
        sx={{
          position: "relative",
          width: 360,
          bgcolor: "#ededed",
          border: "1px solid",
          borderColor: selected ? "#26890D" : "#cfcfcf",
          boxShadow: "0 8px 10px rgba(0,0,0,0.15)",
          overflow: "visible",
          pointerEvents: "all", // 🔥 REQUIRED
        }}
      >
        <Box
          sx={{
            width: 360,
            bgcolor: "#ededed",
            border: "1px solid",
            borderColor: selected ? "#26890D" : "#cfcfcf",
            boxShadow: "0 8px 10px rgba(0,0,0,0.15)",
            overflow: "visible",
            pointerEvents: "all", // 🔥 REQUIRED
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 1.5,
              py: 1,
              backgroundColor: "#26890D",
              color: "#fff",
              display: "flex",
              alignItems: "center",
            }}
          >
            <AccountTreeOutlined fontSize="small" sx={{ mr: 0.5 }} />
            <Typography sx={componentStyle.font.cardHeadder}>
              Question
            </Typography>
          </Box>

          {/* Question text */}
          <Box sx={{ px: 1.5, py: 1 }}>
            <TextField
              variant="standard"
              fullWidth
              multiline
              minRows={1}
              maxRows={6}
              placeholder="Type your question"
              value={questionText}
              onChange={(e) => updateQuestion(e.target.value)}
              onMouseDown={stop} //  REQUIRED
              onClick={stop}
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: 14,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                },
              }}
              sx={{
                backgroundColor: "transparent",

                /* Remove MUI root padding */
                "& .MuiInputBase-root": {
                  padding: 0,
                  backgroundColor: "transparent",
                },

                /* Remove textarea internal padding */
                "& .MuiInputBase-input": {
                  padding: 0,
                  margin: 0,
                  backgroundColor: "transparent",
                  lineHeight: 1.4,
                },

                /* Remove any FormControl spacing */
                "& .MuiFormControl-root": {
                  margin: 0,
                },
              }}
            />
          </Box>

          {/* Answers */}
          <Box sx={{ px: 1 }}>
            {answers.map((answer) => (
              <Box
                key={answer.id}
                className="pm-answer-row"
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "center",
                  px: 0.5,
                  py: 0.75,
                }}
              >
                <TextField
                  variant="standard"
                  multiline
                  minRows={1}
                  maxRows={4}
                  value={answer.label}
                  onChange={(e) => updateAnswer(answer.id, e.target.value)}
                  placeholder="Answer"
                  onMouseDown={stop} //  REQUIRED
                  onClick={stop}
                  InputProps={{
                    disableUnderline: true,
                    sx: {
                      fontSize: 14,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    },
                  }}
                  sx={{
                    flex: 1,
                    backgroundColor: "transparent",

                    /* Remove FormControl spacing */
                    "& .MuiFormControl-root": {
                      margin: 0,
                    },

                    /* Remove InputBase spacing */
                    "& .MuiInputBase-root": {
                      padding: 0,
                      backgroundColor: "transparent",
                    },

                    /* THIS fixes the white patch */
                    "& .MuiInputBase-input": {
                      padding: 0,
                      margin: 0,
                      backgroundColor: "transparent !important",
                    },

                    /* For multiline specifically */
                    "& textarea": {
                      padding: 0,
                      margin: 0,
                      backgroundColor: "transparent !important",
                    },
                  }}
                />

                {/* Branch handle */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={answer.id}
                  className="out-handle"
                />
              </Box>
            ))}

            {/* Add option */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: 0.5,
                py: 0.5,
                cursor: "pointer",
              }}
              onClick={(e) => {
                stop(e); // 🔥 REQUIRED
                addAnswer();
              }}
            >
              <IconButton size="small" onMouseDown={stop}>
                <AddIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption">Add option</Typography>
            </Box>
          </Box>
        </Box>

        {/* Input handle */}
        <Handle
          type="target"
          position={Position.Left}
          id="in"
          className="pm-input-handle"
        />
      </Box>
    </Box>
  );
}

/* -------------------------------------------
   NODE REGISTRATION (unchanged)
------------------------------------------- */
registerNode({
  type: "question",
  label: "Question",
  getDefaultData() {
    return {
      questionText: "",
      questionType: "YES_NO",
      answers: [
        { id: "yes", label: "Yes" },
        { id: "no", label: "No" },
      ],
      restrictions: { lockedToYesNo: true },
    };
  },
  renderNode: QuestionNode,
});

export default QuestionNode;
